/**
 * Prompt Dialog Component
 * 
 * A Promise-based input dialog that replaces window.prompt().
 * Supports text, url, email, password, and number input types
 * with optional validation.
 * 
 * Usage via useFeedback():
 *   const { prompt } = useFeedback();
 *   const url = await prompt.url();
 *   const name = await prompt.text('Enter your name');
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DIALOGS } from '@/design-system/patterns/dialogs';
import { cn } from '@/lib/utils';

export interface PromptOptions {
  title: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  inputType?: 'text' | 'url' | 'email' | 'password' | 'number';
  confirmText: string;
  cancelText: string;
  validate?: (value: string) => true | string; // true or error message
}

export interface PromptDialogProps extends PromptOptions {
  open: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

export const PromptDialog = ({
  open,
  title,
  message,
  placeholder,
  defaultValue = '',
  inputType = 'text',
  confirmText,
  cancelText,
  validate,
  onSubmit,
  onCancel,
}: PromptDialogProps) => {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setError(null);
      // Focus input after dialog animation
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, defaultValue]);

  const handleSubmit = () => {
    if (validate) {
      const result = validate(value);
      if (result !== true) {
        setError(result);
        return;
      }
    }
    onSubmit(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    // Clear error when user types
    if (error) setError(null);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className={cn(DIALOGS.small)}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="space-y-2">
            <Label htmlFor="prompt-input" className="sr-only">
              {title}
            </Label>
            <Input
              ref={inputRef}
              id="prompt-input"
              type={inputType}
              value={value}
              onChange={(e) => handleValueChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={cn(error && 'border-destructive focus-visible:ring-destructive')}
              aria-invalid={!!error}
              aria-describedby={error ? 'prompt-error' : undefined}
            />
            {error && (
              <p id="prompt-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {cancelText}
          </Button>
          <Button onClick={handleSubmit}>
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
